import React from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { injectIntl, FormattedMessage } from 'react-intl'
import { useLocalStorageState } from 'ahooks'
import {
  Button,
  Flex,
  Row,
  Col,
  Empty,
  Badge,
  Radio,
  Table,
  ConfigProvider,
  theme as antdTheme,
  Breadcrumb,
  Input,
} from 'antd'
import { HomeOutlined, LeftOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import objectSupport from 'dayjs/plugin/objectSupport'
import { useFaults } from '../hooks/Faults'
import { useUserPlant, useUserTestZones } from '../hooks/User'
import { openTabWithVehicleTestByResultId } from '../api/vehicle-tests-api'
import { Loadable } from '../components/Loading'
import { groupFaults } from './utils'
import { apiGetTerminology, apiGetTerminologyBatch } from '../api/terminology'
import { locale } from '../Locale'
import iconRedAsterisk from '../assets/images/red-asterisk.png'
import './styles.scss'
import { BRAND_BLUE, WHITE_COLOR, DARK_MODE } from '../constants'
import { isWindowBelowMinHeight } from '../helpers/utils'
import { ReportControl } from './ReportControlForm'
import { MonitorTable } from './MonitorTable'
import uuidv4 from 'uuid/v4'

const tablePropTypes = PropTypes.shape({
  faults: PropTypes.object,
  selectedFault: PropTypes.object,
  setFault: PropTypes.func,
  intl: PropTypes.object,
})

dayjs.extend(objectSupport)
dayjs.extend(utc)

const SEARCH_RESULT_CATEGORY = {
  component: 'component',
  device: 'device',
  harness: 'harness',
  generic: 'generic',
}

function StationAndAlertIcon({ row }) {
  if (row.station_alert === 'send') {
    return (
      <div>
        <img style={{ width: '10px' }} src={iconRedAsterisk} alt="asterisk" />
        <span style={{ marginLeft: '0.5rem' }}>{row.station}</span>
      </div>
    )
  }
  return row.station
}

function countComponentFaults(faults) {
  return faults.reduce((counted, fault) => {
    const key = `${fault.component_id}::${fault.station}::${fault.reason}`
    if (!counted.get(key)) {
      counted.set(key, {
        type: 'component',
        id: fault.id,
        component_id: fault.component_id,
        attachedIssue: fault.attachedIssue,
        relatedIssues: fault.relatedIssues,
        station: fault.station,
        station_alert: fault.station_alert,
        reason: fault.reason,
        component_description: fault.component_description,
        translated_description: fault.translated_description,
        problems: [],
        key,
        test_zone: fault.hardware_location_id,
        short_code: fault.short_code,
        problem_code: fault.problem_code,
        fault_key: fault.fault_key,
        station_id: fault.station_id,
        make_model_id: fault.make_model_id,
      })
    }
    counted.get(key).problems.push({
      type: 'component',
      vin: fault.vin,
      id: fault.id,
      vehicle_test_result_id: fault.vehicle_test_result_id,
      component_id: fault.component_id,
      created_at: fault.created_at,
      note: fault.note,
      created_by: fault.created_by,
      attachedIssue: fault.attachedIssue,
      relatedIssues: fault.relatedIssues,
      test_zone: fault.hardware_location_id,
      short_code: fault.short_code,
      problem_code: fault.problem_code,
      fault_key: fault.fault_key,
      station_id: fault.station_id,
      make_model_id: fault.make_model_id,
    })
    return counted
  }, new Map())
}

function countDeviceFaults(faults) {
  return faults.reduce((counted, fault) => {
    const key = `${fault.device_name}::${fault.reason}`
    if (!counted.get(key)) {
      counted.set(key, {
        type: 'device',
        id: fault.id,
        device_name: fault.device_name,
        reason: fault.reason,
        description: fault.description,
        translated_description: fault.translated_description,
        problems: [],
        key,
        attachedIssue: fault.attachedIssue,
        relatedIssues: fault.relatedIssues,
        test_zone: fault.hardware_location_id,
        short_code: fault.short_code,
        problem_code: fault.problem_code,
        fault_key: fault.fault_key,
        station_id: fault.station_id,
        make_model_id: fault.make_model_id,
      })
    }
    counted.get(key).problems.push({
      vin: fault.vin,
      id: fault.id,
      vehicle_test_result_id: fault.vehicle_test_result_id,
      created_at: fault.created_at,
      note: fault.note,
      created_by: fault.created_by,
      attachedIssue: fault.attachedIssue,
      relatedIssues: fault.relatedIssues,
      test_zone: fault.hardware_location_id,
      short_code: fault.short_code,
      problem_code: fault.problem_code,
      fault_key: fault.fault_key,
      station_id: fault.station_id,
      make_model_id: fault.make_model_id,
    })
    return counted
  }, new Map())
}

function countHarnessFaults(faults) {
  return faults.reduce((counted, fault) => {
    const key = `${fault.harness}::${fault.description}::${fault.reason}`
    if (!counted.get(key)) {
      counted.set(key, {
        type: 'harness',
        id: fault.id,
        harness: fault.harness,
        description: fault.description,
        translated_description: fault.translated_description,
        reason: fault.reason,
        problems: [],
        key,
        attachedIssue: fault.attachedIssue,
        relatedIssues: fault.relatedIssues,
        test_zone: fault.hardware_location_id,
        short_code: fault.short_code,
        problem_code: fault.problem_code,
        fault_key: fault.fault_key,
        station_id: fault.station_id,
        make_model_id: fault.make_model_id,
      })
    }
    counted.get(key).problems.push({
      vin: fault.vin,
      vehicle_test_result_id: fault.vehicle_test_result_id,
      created_at: fault.created_at,
      note: fault.note,
      created_by: fault.created_by,
      id: fault.id,
      attachedIssue: fault.attachedIssue,
      relatedIssues: fault.relatedIssues,
      test_zone: fault.hardware_location_id,
      short_code: fault.short_code,
      problem_code: fault.problem_code,
      fault_key: fault.fault_key,
      station_id: fault.station_id,
      make_model_id: fault.make_model_id,
    })
    return counted
  }, new Map())
}

function countTesterFaults(faults) {
  return faults.reduce((counted, fault) => {
    const key = `${fault.reason}`
    if (!counted.get(key)) {
      counted.set(key, {
        type: 'tester',
        reason: fault.reason,
        id: fault.id,
        problems: [],
        key,
        attachedIssue: fault.attachedIssue,
        relatedIssues: fault.relatedIssues,
        test_zone: fault.hardware_location_id,
        short_code: fault.short_code,
        problem_code: fault.problem_code,
        fault_key: fault.fault_key,
        station_id: fault.station_id,
        make_model_id: fault.make_model_id,
      })
    }
    counted.get(key).problems.push({
      vin: fault.vin,
      vehicle_test_result_id: fault.vehicle_test_result_id,
      created_at: fault.created_at,
      note: fault.note,
      created_by: fault.created_by,
      attachedIssue: fault.attachedIssue,
      relatedIssues: fault.relatedIssues,
      id: fault.id,
      test_zone: fault.hardware_location_id,
      short_code: fault.short_code,
      problem_code: fault.problem_code,
      fault_key: fault.fault_key,
      station_id: fault.station_id,
      make_model_id: fault.make_model_id,
    })
    return counted
  }, new Map())
}

const tableSorter = (current, previous) =>
  current.problems.length - previous.problems.length

function ComponentFaults({
  faults,
  selectedFault,
  setFault,
  intl,
  componentTableSortingObject,
}) {
  const columns = [
    {
      title: intl.formatMessage({
        id: 'faultReport.componentFaultsHeaders.components',
      }),
      render: (text, record) =>
        `${record.component_id}\n${
          record.translated_description
            ? record.translated_description
            : record.component_description
        }`,
      ellipsis: true,
      width: '25%',
    },
    {
      title: intl.formatMessage({
        id: 'faultReport.componentFaultsHeaders.station',
      }),
      render: (text, record) => StationAndAlertIcon({ row: record }),
      width: '15%',
    },
    {
      title: intl.formatMessage({
        id: 'faultReport.componentFaultsHeaders.reason',
      }),
      dataIndex: ['reason'],
      ellipsis: true,
      width: '23%',
    },
    {
      title: intl.formatMessage({
        id: 'faultReport.componentFaultsHeaders.count',
      }),
      render: (text, record) => record.problems.length,
      width: '10%',
      ...componentTableSortingObject,
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={faults}
      id="component-faults"
      rowSelection={{
        type: 'radio',
        onSelect: (record) => setFault(record),
        selectedRowKeys: [selectedFault.key],
      }}
      pagination={{
        pageSize: isWindowBelowMinHeight() ? 2 : 4,
        hideOnSinglePage: true,
        showSizeChanger: false,
      }}
    />
  )
}

function TesterFaults({
  faults,
  selectedFault,
  setFault,
  intl,
  otherTableSortingObject,
}) {
  const columns = React.useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'faultReport.testerFaultsHeaders.header',
        }),
        dataIndex: ['reason'],
        width: '60%',
      },
      {
        title: intl.formatMessage({
          id: 'faultReport.testerFaultsHeaders.count',
        }),
        render: (text, record) => record.problems.length,
        ...otherTableSortingObject,
      },
    ],
    [selectedFault, setFault],
  )
  return (
    <Table
      columns={columns}
      dataSource={faults}
      id="other-faults"
      rowSelection={{
        type: 'radio',
        onSelect: (record) => setFault(record),
        selectedRowKeys: [selectedFault.key],
      }}
      pagination={{
        pageSize: isWindowBelowMinHeight() ? 2 : 4,
        hideOnSinglePage: true,
        showSizeChanger: false,
      }}
    />
  )
}

function HarnessFaults({
  faults,
  selectedFault,
  setFault,
  intl,
  harnessTableSortingObject,
}) {
  const columns = React.useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'faultReport.harnessFaultsHeaders.harness',
        }),
        render: (text, record) =>
          `${record.harness}\n${
            record.translated_description
              ? record.translated_description
              : record.description
          }`,
        width: '24%',
        ellipsis: true,
      },
      {
        title: intl.formatMessage({
          id: 'faultReport.harnessFaultsHeaders.reason',
        }),
        dataIndex: ['reason'],
        ellipsis: true,
        width: '16%',
      },
      {
        title: intl.formatMessage({
          id: 'faultReport.harnessFaultsHeaders.description',
        }),
        dataIndex: ['description'],
        ellipsis: true,
        width: '22%',
      },
      {
        title: intl.formatMessage({
          id: 'faultReport.harnessFaultsHeaders.count',
        }),
        render: (text, record) => record.problems.length,
        width: '10%',
        ...harnessTableSortingObject,
      },
    ],
    [selectedFault, setFault],
  )
  return (
    <Table
      columns={columns}
      dataSource={faults}
      id="harness-faults"
      rowSelection={{
        type: 'radio',
        onSelect: (record) => setFault(record),
        selectedRowKeys: [selectedFault.key],
      }}
      pagination={{
        pageSize: isWindowBelowMinHeight() ? 2 : 4,
        hideOnSinglePage: true,
        showSizeChanger: false,
      }}
    />
  )
}

function DeviceFaults({
  faults,
  selectedFault,
  setFault,
  intl,
  deviceTableSortingObject,
}) {
  const columns = React.useMemo(
    () => [
      {
        title: intl.formatMessage({
          id: 'faultReport.deviceFaultsHeaders.device',
        }),
        render: (text, record) => {
          const description = record.translated_description
            ? record.translated_description
            : record.description
              ? record.description
              : ''
          return `${record.device_name}\n${description}`
        },
        width: '24%',
        ellipsis: true,
      },
      {
        title: intl.formatMessage({
          id: 'faultReport.deviceFaultsHeaders.reason',
        }),
        width: '21%',
        dataIndex: ['reason'],
        ellipsis: true,
      },
      {
        title: intl.formatMessage({
          id: 'faultReport.deviceFaultsHeaders.description',
        }),
        dataIndex: ['description'],
        width: '18%',
        ellipsis: true,
      },
      {
        title: intl.formatMessage({
          id: 'faultReport.deviceFaultsHeaders.count',
        }),
        render: (text, record) => record.problems.length,
        width: '11%',
        ...deviceTableSortingObject,
      },
    ],
    [selectedFault, setFault],
  )
  return (
    <Table
      columns={columns}
      dataSource={faults}
      id="device-faults"
      rowSelection={{
        type: 'radio',
        onSelect: (record) => setFault(record),
        selectedRowKeys: [selectedFault.key],
      }}
      pagination={{
        pageSize: isWindowBelowMinHeight() ? 2 : 4,
        hideOnSinglePage: true,
        showSizeChanger: false,
      }}
    />
  )
}

export const VinURL = ({ vin, vehicleTestResultId, componentId }) => {
  return (
    <a
      className="report-vin-anchor"
      onClick={(event) => {
        event.preventDefault()
        openTabWithVehicleTestByResultId({ vehicleTestResultId, componentId })
      }}
    >
      {vin}
    </a>
  )
}

function VehicleTestTable({ data, intl }) {
  const columns = [
    {
      title: intl.formatMessage({
        id: 'faultReport.vehicleTestTableHeaders.vin',
      }),
      render: (text, record) => (
        <VinURL
          vin={record.vin}
          vehicleTestResultId={record.vehicle_test_result_id}
          componentId={record.type === 'component' ? record.component_id : null}
        />
      ),
      width: '22%',
    },
    {
      title: intl.formatMessage({
        id: 'faultReport.vehicleTestTableHeaders.timestamp',
      }),
      render: (text, record) => {
        return (
          <>
            {record.test_zone}
            <br />
            <span className="created-at-lbl">
              {dayjs(record.created_at).format('MM/DD/YYYY h:mma')}
            </span>
          </>
        )
      },
    },
    {
      title: intl.formatMessage({
        id: 'faultReport.vehicleTestTableHeaders.notes',
      }),
      dataIndex: ['note'],
      ellipsis: true,
    },
    {
      title: intl.formatMessage({
        id: 'faultReport.vehicleTestTableHeaders.userId',
      }),
      dataIndex: ['created_by'],
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={data}
      id="report-vehicle-tests"
      pagination={{
        pageSize: 2,
        hideOnSinglePage: true,
        showSizeChanger: false,
      }}
    />
  )
}

const SelectedFault = ({ fault, intl }) => {
  let text = ''
  if (fault.type === 'component') {
    text = `${intl.formatMessage({
      id: 'faultReport.selectedFaultText.component',
    })} "${fault.component_id} - ${fault.component_description} - ${
      fault.reason
    }"`
  } else if (fault.type === 'harness') {
    text = `${intl.formatMessage({
      id: 'faultReport.selectedFaultText.harness',
    })} "${fault.harness} - ${fault.reason}"`
  } else if (fault.type === 'device') {
    text = `${intl.formatMessage({
      id: 'faultReport.selectedFaultText.device',
    })} "${fault.device_name} - ${fault.reason}"`
  } else if (fault.type === 'tester') {
    text = `${intl.formatMessage({
      id: 'faultReport.selectedFaultText.tester',
    })} ${fault.reason}`
  }

  return (
    <div id="selected-fault">
      {intl.formatMessage({ id: 'faultReport.selectedFaultText.selected' })}:{' '}
      {text}
    </div>
  )
}

const Counter = ({ isLoading, results, theme }) => {
  return (
    <Loadable isLoading={isLoading} showText={false}>
      <Badge
        color={theme == DARK_MODE ? 'white' : '#0189e9'}
        count={results ? results.length : 0}
        showZero
      />
    </Loadable>
  )
}

const GenerateTerminology = (faults) =>
  new Promise((resolve) => {
    let requestBody = {}
    let faultObject = {}
    faults.forEach((fault) => {
      let faultKey
      switch (fault.type) {
        case 'component':
          faultKey = fault.component_id
          if (requestBody[fault.vin]) {
            requestBody[fault.vin].push(fault.component_id)
          } else {
            requestBody[fault.vin] = [fault.component_id]
          }
          break
        case 'device':
          faultKey = fault.device_name
          if (requestBody[fault.vin]) {
            requestBody[fault.vin].push(fault.device_name)
          } else {
            requestBody[fault.vin] = [fault.device_name]
          }
          break
        case 'harness':
          faultKey = fault.harness
          if (requestBody[fault.vin]) {
            requestBody[fault.vin].push(fault.harness)
          } else {
            requestBody[fault.vin] = [fault.harness]
          }
          break
      }
      if (faultObject[faultKey]) {
        faultObject[faultKey].push(fault)
      } else {
        faultObject[faultKey] = [fault]
      }
    })

    const termData = []
    for (const [key, value] of Object.entries(requestBody)) {
      termData.push({
        vin: key,
        codes: value,
        language: locale,
      })
    }
    apiGetTerminologyBatch(termData).then((results) => {
      for (const [code, termInfo] of Object.entries(results)) {
        for (let i = 0; i < faultObject[code].length; i++) {
          switch (faultObject[code][i].type) {
            case 'component':
              if (faultObject[code][i].component_id === code) {
                faultObject[code][i]['translated_description'] =
                  termInfo['description']
              }
              break
            case 'device':
              if (faultObject[code][i].device_name === code) {
                faultObject[code][i]['translated_description'] =
                  termInfo['description']
              }
              break
            case 'harness':
              if (faultObject[code][i].harness === code) {
                faultObject[code][i]['translated_description'] =
                  termInfo['description']
              }
              break
            default:
              break
          }
        }
      }
      resolve(Object.values(faultObject).flat())
    })
  })

function Faults({ report, periods, intl }) {
  const [selectedFault, setFault] = React.useState('')
  const theme = useSelector((state) => state.app.theme, shallowEqual)
  const [componentFaults, updateComponentFaults] = React.useState([])
  const [harnessFaults, updateHarnessFaults] = React.useState([])
  const [testerFaults, updateTesterFaults] = React.useState([])
  const [deviceFaults, updateDeviceFaults] = React.useState([])
  const [isLoadingTerminology, updateLoadingStatus] = React.useState(true)
  const [searchStr, updateSearchStr] = React.useState('')
  const [searchResultCategory, setSearchResultCategory] = React.useState(
    SEARCH_RESULT_CATEGORY.component,
  )

  const [faults, updateFaults] = React.useState({
    component: [],
    harness: [],
    other: [],
    device: [],
  })

  React.useEffect(() => {
    const initialFaults = getFaultsFromPeriod()
    setFault('')
    updateSearchStr('')
    updateLoadingStatus(true)
    const requestBody = [
      ...initialFaults.component,
      ...initialFaults.device,
      ...initialFaults.harness,
    ]
    if (requestBody.length != 0) {
      GenerateTerminology(requestBody).then((results) => {
        const updatedFaultList = getUpdatedFaultList(results, initialFaults)
        updateFaults((faults) => {
          faults.component = [
            ...countComponentFaults(updatedFaultList.component).values(),
          ]
          faults.harness = [
            ...countHarnessFaults(updatedFaultList.harness).values(),
          ]
          faults.other = [...countTesterFaults(updatedFaultList.other).values()]
          faults.device = [
            ...countDeviceFaults(updatedFaultList.device).values(),
          ]
          return faults
        })
        updateFilteredFaults(updatedFaultList)
        updateLoadingStatus(false)
      })
    } else {
      updateFaults(initialFaults)
      updateFilteredFaults(initialFaults)
      updateLoadingStatus(false)
    }
  }, [periods])

  const getUpdatedFaultList = (results, faults) => {
    const updatedFaultList = {
      component: [],
      harness: [],
      other: faults.other,
      device: [],
    }
    results.forEach((result) => {
      switch (result.type) {
        case 'component':
          updatedFaultList.component.push(result)
          break
        case 'device':
          updatedFaultList.device.push(result)
          break
        case 'harness':
          updatedFaultList.harness.push(result)
          break
      }
    })
    return updatedFaultList
  }

  const getFaultsFromPeriod = () => {
    return periods.reduce(
      (acc, period) => {
        acc.component.push(...report[period.day][period.hour].component)
        acc.harness.push(...report[period.day][period.hour].harness)
        acc.other.push(...report[period.day][period.hour].other)
        acc.device.push(...report[period.day][period.hour].device)
        return acc
      },
      { component: [], harness: [], other: [], device: [] },
    )
  }
  const onSearch = () => {
    const filteredFaults = { ...faults }
    const searchStrLower = searchStr.toLocaleLowerCase()
    let foundSelectFault = false

    filteredFaults.component = filteredFaults.component.filter((fault) => {
      const faultSearch =
        fault.component_description
          .toLocaleLowerCase()
          .includes(searchStrLower) ||
        fault.component_id.toLocaleLowerCase().includes(searchStrLower) ||
        fault.reason.toLocaleLowerCase().includes(searchStrLower) ||
        (fault.translated_description &&
          fault.translated_description
            .toLocaleLowerCase()
            .includes(searchStrLower))

      if (faultSearch && fault.id == selectedFault.id) {
        foundSelectFault = true
      }

      return faultSearch
    })

    filteredFaults.harness = filteredFaults.harness.filter((fault) => {
      const faultSearch =
        (fault.description &&
          fault.description.toLocaleLowerCase().includes(searchStrLower)) ||
        fault.harness.toLocaleLowerCase().includes(searchStrLower) ||
        fault.reason.toLocaleLowerCase().includes(searchStrLower) ||
        (fault.translated_description &&
          fault.translated_description
            .toLocaleLowerCase()
            .includes(searchStrLower))

      if (faultSearch && fault.id == selectedFault.id) {
        foundSelectFault = true
      }
      return faultSearch
    })

    filteredFaults.device = filteredFaults.device.filter((fault) => {
      const faultSearch =
        (fault.description &&
          fault.description.toLocaleLowerCase().includes(searchStrLower)) ||
        fault.device_name.toLocaleLowerCase().includes(searchStrLower) ||
        fault.reason.toLocaleLowerCase().includes(searchStrLower) ||
        (fault.translated_description &&
          fault.translated_description
            .toLocaleLowerCase()
            .includes(searchStrLower))

      if (faultSearch && fault.id == selectedFault.id) {
        foundSelectFault = true
      }
      return faultSearch
    })

    filteredFaults.other = filteredFaults.other.filter((fault) => {
      const faultSearch = fault.reason
        .toLocaleLowerCase()
        .includes(searchStrLower)
      if (faultSearch && fault.id == selectedFault.id) {
        foundSelectFault = true
      }
      return faultSearch
    })
    if (!foundSelectFault) {
      setFault('')
    }
    updateComponentFaults(filteredFaults.component)
    updateHarnessFaults(filteredFaults.harness)
    updateTesterFaults(filteredFaults.other)
    updateDeviceFaults(filteredFaults.device)
  }

  const updateFilteredFaults = (filteredFaults) => {
    updateComponentFaults([
      ...countComponentFaults(filteredFaults.component).values(),
    ])
    updateHarnessFaults([
      ...countHarnessFaults(filteredFaults.harness).values(),
    ])
    updateTesterFaults([...countTesterFaults(filteredFaults.other).values()])
    updateDeviceFaults([...countDeviceFaults(filteredFaults.device).values()])
  }

  const onSearchValueChange = (event) => {
    updateSearchStr(event.target.value)
  }

  const asteriskLegend = intl.formatMessage({
    id: 'faultReport.selectedPeriodsInfo.asterisk',
  })

  const showSelectedTable = () => {
    const sortingObject = {
      defaultSortOrder: 'descend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: tableSorter,
    }
    switch (searchResultCategory) {
      case 'component':
        return (
          <ComponentFaults
            faults={componentFaults}
            selectedFault={selectedFault}
            setFault={setFault}
            intl={intl}
            componentTableSortingObject={sortingObject}
          />
        )
      case 'device':
        return (
          <DeviceFaults
            faults={deviceFaults}
            selectedFault={selectedFault}
            setFault={setFault}
            intl={intl}
            deviceTableSortingObject={sortingObject}
          />
        )
      case 'harness':
        return (
          <HarnessFaults
            faults={harnessFaults}
            selectedFault={selectedFault}
            setFault={setFault}
            intl={intl}
            harnessTableSortingObject={sortingObject}
          />
        )
      case 'generic':
        return (
          <TesterFaults
            faults={testerFaults}
            selectedFault={selectedFault}
            setFault={setFault}
            intl={intl}
            otherTableSortingObject={sortingObject}
          />
        )
    }
  }

  return (
    <>
      <Loadable isLoading={isLoadingTerminology} showText={false}>
        <Flex vertical={false} gap="middle">
          <Input
            data-testid="search-string"
            placeholder={intl.formatMessage({
              id: 'faultReport.inputSearchTextPlaceholder',
            })}
            allowClear
            onPressEnter={onSearch}
            onChange={onSearchValueChange}
            value={searchStr}
          />
          <Button onClick={onSearch} type="primary">
            <FormattedMessage id="faultReport.search" />
          </Button>
        </Flex>
        <Radio.Group
          className="report-switch"
          value={searchResultCategory}
          buttonStyle="solid"
          size="middle"
          onChange={(event) => setSearchResultCategory(event.target.value)}
        >
          <Radio.Button value={SEARCH_RESULT_CATEGORY.component}>
            <Flex
              data-testid="results-switch-components"
              vertical={false}
              gap="middle"
              className="switch-radio-option"
            >
              <FormattedMessage id="faultReport.componentFaultsHeaders.components" />
              <Counter results={componentFaults} theme={theme} />
            </Flex>
          </Radio.Button>
          <Radio.Button value={SEARCH_RESULT_CATEGORY.device}>
            <Flex
              data-testid="results-switch-devices"
              vertical={false}
              gap="middle"
              className="switch-radio-option"
            >
              <FormattedMessage id="faultReport.deviceFaultsHeaders.device" />
              <Counter results={deviceFaults} theme={theme} />
            </Flex>
          </Radio.Button>
          <Radio.Button value={SEARCH_RESULT_CATEGORY.harness}>
            <Flex
              vertical={false}
              gap="middle"
              data-testid="results-switch-harness"
              className="switch-radio-option"
            >
              <FormattedMessage id="faultReport.harnessFaultsHeaders.harness" />
              <Counter results={harnessFaults} theme={theme} />
            </Flex>
          </Radio.Button>
          <Radio.Button value={SEARCH_RESULT_CATEGORY.generic}>
            <Flex
              data-testid="results-switch-generic"
              vertical={false}
              gap="middle"
              className="switch-radio-option"
            >
              <FormattedMessage id="faultReport.testerFaultsHeaders.header" />
              <Counter results={testerFaults} theme={theme} />
            </Flex>
          </Radio.Button>
        </Radio.Group>
        <Flex gap={30} vertical={true} className="reports-table-container">
          <Flex vertical={true} className="fault-table">
            <span className="fault-report-legend">
              <img
                style={{ width: '10px' }}
                src={iconRedAsterisk}
                alt="asterisk"
              />
              {asteriskLegend}
            </span>
            {showSelectedTable()}
          </Flex>
          <Flex vertical={true} className="selected-fault-table">
            <SelectedFault fault={selectedFault} intl={intl} />
            <VehicleTestTable data={selectedFault.problems} intl={intl} />
          </Flex>
        </Flex>
      </Loadable>
    </>
  )
}

function ReportsPage({ intl, isMonitor }) {
  const { defaultAlgorithm } = antdTheme
  const [searchTerm, setSearchTerm] = useLocalStorageState('reports-query', {
    defaultValue: {
      stationIds: [],
      makeModelIds: [],
      zones: [],
      teams: [],
      department: null,
      start_at: null,
      end_at: null,
      shift: null,
      period: { start: null, end: null },
      reason: null,
      vinStatus: null,
    },
    listenStorageChange: true,
  })
  const history = useHistory()
  let {
    makeModelIds,
    stationIds,
    start_at,
    end_at,
    zones,
    teams,
    department,
    reason,
    vinStatus,
  } = searchTerm
  const [station_alerts] = useLocalStorageState('station-alert', {
    defaultValue: 'send,skip',
  })
  const [plants] = useUserPlant()
  const [testZones] = useUserTestZones()
  const { isSuccess, isLoading, isFetching, faults } = useFaults({
    makeModelIds,
    stationIds,
    start_at,
    end_at,
    station_alerts,
    plants,
    zones,
    teams,
    department,
    testZones: !!testZones ? testZones.map((tz) => tz.specifier) : [],
    reason,
    vinStatus: vinStatus,
  })
  const [periods, setPeriods] = React.useState([])
  const [report, setReport] = React.useState({})
  const [refreshInterval, setRefreshInterval] = React.useState(60000) // 60 seconds
  const [previousShiftTimes, setPreviousShiftTimes] = React.useState(null)

  React.useEffect(() => {
    if (!faults) return
    setReport(groupFaults(faults))
    return () => setPeriods([])
  }, [faults])

  React.useEffect(() => {
    if (!report) return
    setPeriods(getAllPeriods(report))
  }, [report])

  const getAllPeriods = (report) => {
    const allPeriods = []
    for (const [day, hours] of Object.entries(report)) {
      for (const hour of Object.keys(hours)) {
        allPeriods.push({ day, hour })
      }
    }
    return allPeriods
  }

  const sessionId = uuidv4()
  return (
    <Flex vertical={true} id="reports-page">
      <Flex
        vertical={false}
        justify="flex-start"
        gap={10}
        className="reports-breadcrumb-wrapper"
      >
        <LeftOutlined onClick={() => history.push('/reports')} />
        <div>
          <Breadcrumb>
            <Breadcrumb.Item>
              <HomeOutlined />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <FormattedMessage id="faultReport.header" />
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </Flex>
      <Row className="reports-page-body">
        <Col span={24} className="reports-page-body-left">
          <ConfigProvider
            theme={{
              algorithm: defaultAlgorithm,
              components: {
                Collapse: {
                  headerBg: '#f2f2f2',
                  // This section forces the light algorithm above, but that
                  // only recomputes token *defaults* -- it doesn't clear an
                  // explicit override from an ancestor ConfigProvider. The
                  // app-wide theme (ComponentThemeGenerator.js) now sets
                  // Collapse.contentBg per dark/light mode, which otherwise
                  // leaks the dark-mode value in here. Pin it explicitly so
                  // this filter panel stays light regardless of app theme.
                  contentBg: WHITE_COLOR,
                },
                Input: {
                  colorBgContainer: WHITE_COLOR,
                },
                Button: {
                  colorBgContainer: WHITE_COLOR,
                  defaultHoverBg: WHITE_COLOR,
                  colorPrimary: BRAND_BLUE,
                  primaryColor: WHITE_COLOR,
                  colorPrimaryHover: '#4096ff',
                },
                Select: {
                  colorBgContainer: WHITE_COLOR,
                  colorBgElevated: WHITE_COLOR,
                },
              },
            }}
          >
            <ReportControl
              intl={intl}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              faults={faults}
              isMonitor={isMonitor}
              setRefreshInterval={setRefreshInterval}
              previousShiftTimes={previousShiftTimes}
            />
          </ConfigProvider>
        </Col>
      </Row>
      <Row className="reports-page-body">
        <Col span={24} className="reports-page-body-left">
          {isMonitor && (
            <MonitorTable
              refreshInterval={refreshInterval}
              searchTerm={searchTerm}
              sessionId={sessionId}
              onPreviousShiftComputed={setPreviousShiftTimes}
            />
          )}
          {!isMonitor &&
            (periods?.length > 0 && Object.keys(report).length > 0 ? (
              <Faults report={report} periods={periods} intl={intl} />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={intl.formatMessage({
                  id: 'faultReport.noSelection',
                })}
              />
            ))}
        </Col>
      </Row>
    </Flex>
  )
}

ComponentFaults.propTypes = PropTypes.oneOfType([
  tablePropTypes,
  PropTypes.shape({ componentTableSortingObject: PropTypes.object }),
])
TesterFaults.propTypes = PropTypes.oneOfType([
  tablePropTypes,
  PropTypes.shape({ otherTableSortingObject: PropTypes.object }),
])
HarnessFaults.propTypes = PropTypes.oneOfType([
  tablePropTypes,
  PropTypes.shape({ harnessTableSortingObject: PropTypes.object }),
])
DeviceFaults.propTypes = PropTypes.oneOfType([
  tablePropTypes,
  PropTypes.shape({ deviceTableSortingObject: PropTypes.object }),
])

export default injectIntl(ReportsPage)
